import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import BookManager from "../../../services/home/BookManager";
import config from "../../../config";
import { motion } from "framer-motion";
import BookReviews from "./BookReviews";
import { ReactComponent as Vector } from "../../../icons/Vector.svg";
import { ReactComponent as Book } from "../../../icons/Book.svg";
import { BookOpen } from "lucide-react";
import { useSelector } from "react-redux";
import { AddToWishlistButton } from "../homePageComponents/ButtonComponents";
import { Share2 } from "lucide-react";
import { toast } from "react-hot-toast";
import { RentNowButton } from "../homePageComponents/RentNowButton";
import { AddToCartButton } from "../homePageComponents/ButtonComponents";
import ReminderManager from "../../../services/home/ReminderManager";
import { Link } from "react-router-dom";

const BookDetails = () => {
  const { id } = useParams();
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const userId = useSelector((state) => state.auth.user?.userId) || null;

  useEffect(() => {
    async function fetchBook() {
      try {
        // <-- Get userId from localStorage (or however you store it)
        const data = await BookManager.getBookById(id, userId); // <-- Pass userId
        setBook(data);
      } catch (error) {
        console.error("Failed to fetch book:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchBook();
  }, [id]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[70vh]">
        <div className="text-primary text-lg animate-bounce">
          Loading book...
        </div>
      </div>
    );
  }

  if (!book) {
    return (
      <div className="flex justify-center items-center min-h-[70vh]">
        <div className="text-red-500 text-lg font-semibold">
          Book not found.
        </div>
      </div>
    );
  }

  const handleShare = (book) => {
    const shareUrl = `${window.location.origin}/book/${book.bookId}`;
    if (navigator.share) {
      navigator.share({
        title: book.bookName,
        text: "Check out this book!",
        url: shareUrl,
      });
    } else {
      navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied to clipboard!");
    }
  };

  const handleReminder = async () => {
    if (!userId) {
      toast.error("Please login to set a reminder.");
      return;
    }

    try {
      const res = await ReminderManager.createReminder(userId, bookId);
      toast.success("Reminder set successfully!");
    } catch (error) {
      console.error("Failed to set reminder:", error);
      toast.error("Failed to set reminder.");
    }
  };

  const {
    bookId,
    bookImages = [],
    bookName,
    authorName,
    rating,
    bookDescription,
    bookIsbn10,
    bookStockStatus,
  } = book;

  const readingAgeDisplayMap = {
    BABY: "0 and above",
    TODDLER: "3 and above",
    CHILD: "6 and above",
    PRE_TEEN: "10 and above",
    TEEN: "13 and above",
    ADULT: "18 and above",
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="p-4 md:p-8 max-w-7xl mx-auto font-nunito"
    >
      <div className="bg-primary-shade1 rounded-3xl shadow-sm p-8 md:p-12 flex flex-col gap-10">
        {/* Top Section: Image + Info */}
        <div className="flex flex-col md:flex-row gap-10 relative">
          {/* Wishlist button - top right */}
          {/* Desktop view */}
          <div className="hidden md:flex items-center gap-2 absolute top-0 right-0 scale-150">
            <AddToWishlistButton book={{ id: book.bookId }} size={56} />
            <button
              onClick={() => handleShare(book)}
              className="text-primary hover:text-primary-shade4 transition px-2"
            >
              <Share2 className="w-5 h-5" />
            </button>
          </div>

          {/* Mobile FAB buttons */}
          <div className="md:hidden fixed bottom-4 right-4 z-50 flex flex-col items-end gap-3">
            <button
              onClick={() => handleShare(book)}
              className="bg-primary text-white rounded-full shadow-lg w-12 h-12 flex items-center justify-center"
              aria-label="Share"
            >
              <Share2 className="w-5 h-5" />
            </button>

            <div className="bg-white text-primary shadow-lg rounded-full w-12 h-12 flex items-center justify-center">
              <AddToWishlistButton book={{ id: book.bookId }} />
            </div>
          </div>

          <motion.div
            whileHover={{ scale: 1.02 }}
            className="flex justify-center"
          >
            <img
              src={
                book.bookImages?.[0] &&
                book.bookImages[0] !== "defaultImage.jpg"
                  ? `${config.url}${book.bookImages[0]}`
                  : "/images/defaultBook.png"
              }
              alt={book.bookName}
              className="w-56 h-80 object-cover rounded-2xl shadow-lg"
            />
          </motion.div>

          {/* Book Info */}
          <div className="flex-1 flex flex-col justify-between">
            <div className="space-y-3">
            <h1 className="text-3xl font-bold text-text-muted leading-tight">
      {
        book.bookName.includes("[")
          ? <>
              {book.bookName.split("[")[0].trim()}
              <br />
              <span className="text-xl text-gray-700 font-semibold">
                [{book.bookName.split("[")[1]?.replace("]", "").trim()}]
              </span>
            </>
          : book.bookName
      }
    </h1>
              <p className="text-gray-600 flex flex-wrap items-center gap-2 text-sm">
                <span><strong>Author:</strong> {book.author?.authorName}</span>
                <span className="text-gray-400">|</span>
                <span><strong>ISBN:</strong> {book.bookIsbn13 || "N/A"}</span>
                <span className="text-gray-400">|</span>
                <span><strong>Category:</strong> {book.category?.catName}</span>
                <span className="text-gray-400">|</span>
                <span><strong>Language:</strong> {book.bookLanguage || "N/A"}</span>
              </p>
              <p className="text-sm font-semibold">
                <strong className="text-black">Stock:</strong>{" "}
                <span
                  className={
                    book.bookStockStatus === "IN_STOCK"
                      ? "text-green-600"
                      : "text-red-600"
                  }
                >
                  {book.bookStockStatus?.replaceAll("_", " ")}
                </span>
              </p>

              <p
                className={`text-gray-700 leading-relaxed mt-4 ${
                  isExpanded ? "" : "line-clamp-5"
                }`}
              >
                {book.bookDescription}
              </p>

              {book.bookDescription?.split(" ").length > 100 && (
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="mt-2 text-primary font-medium hover:underline"
                >
                  {isExpanded ? "Read Less" : "Read More"}
                </button>
              )}
            </div>

            {/* Buttons */}
            <div className="flex gap-3 mt-8">
              <div className="w-24 sm:w-36">
                <RentNowButton
                  book={{
                    id: bookId,
                    image: bookImages[0] || "",
                    title: bookName,
                    author: authorName,
                    isbn: bookIsbn10,
                    inStock: bookStockStatus,
                  }}
                  className="w-full text-xs py-2"
                />
              </div>
              <div className="w-24 sm:w-36">
                <AddToCartButton
                  book={{
                    id: bookId,
                    image: bookImages[0] || "",
                    title: bookName,
                    author: authorName,
                    isbn: bookIsbn10,
                  }}
                  className="w-full text-xs py-2"
                />
              </div>
              {bookStockStatus === "OUT_OF_STOCK" && (
                <div className="w-24 sm:w-36">
                  <button
                    onClick={handleReminder}
                    className="w-full bg-yellow-500 hover:bg-yellow-600 text-white text-xs font-semibold py-2 px-4 rounded-lg transition"
                  >
                    Remind Me
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-300" />

        {/* Book Details */}
        <div>
          <h2 className="text-2xl font-semibold text-text-muted mb-6 flex items-center gap-2">
            <Book className="w-7 h-7 text-primary" />
            Book Details
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-700 text-sm">
            <p>
              <strong>ISBN (10):</strong> {book.bookIsbn10 || "N/A"}
            </p>
            <p>
              <strong>Size:</strong> Small
            </p>
            <p>
              <strong>Edition:</strong> {book.bookEdition}
            </p>
            <p>
              <strong>Genre:</strong> {book.bookGenres}
            </p>
            <p>
              <p>
                <strong>Reading Age:</strong>{" "}
                {readingAgeDisplayMap[book.readingAge] || "-"}
              </p>
            </p>
            <p>
              <strong>Publisher:</strong> {book.bookPublication}
            </p>
            <p>
              <strong>Pages:</strong> {book.pages}
            </p>
            <p>
              <strong>Language:</strong> {book.bookLanguage}
            </p>
            <p>
              <strong>Publication Date:</strong>{" "}
              {new Date(book.publishedDate).toLocaleDateString()}
            </p>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-300" />
        {/* About the Author */}
        <div>
          <h2 className="text-2xl font-semibold text-text-muted mb-6 flex items-center gap-2">
            <Vector className="w-7 h-7 text-primary" />
            About the Author
          </h2>

          <div className="flex flex-col sm:flex-row items-center gap-6">
            <Link to={`/author/${book.author?.authorId}`}>
              <img
                src={`${config.url}/authorImages/${book.author?.authorImage}`}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "/images/default_author_img.png";
                }}
                alt={book.author?.authorName}
                className="w-28 h-28 min-w-[7rem] min-h-[7rem] rounded-full object-cover shadow-md hover:scale-105 transition-transform duration-200"
              />
            </Link>

            <p className="text-gray-600 leading-relaxed text-sm">
              {book.author?.aboutAuthor}
            </p>
          </div>
        </div>
      </div>
      <BookReviews />
    </motion.div>
  );
};

export default BookDetails;
